import { GridColDef } from "@mui/x-data-grid";
import { IconButton } from "@mui/material"; 
import ItemStatus from "../../../../../../common/components/item_status";
import dataUtils from "../../../../../../common/utils/dataUtils";
import DynamicIcon from "../../../../../../common/components/dynamic_icon"
import LargeItemStatus from "../../../../../../common/components/large_item_status";
type AllowedColors = "green" | "red" | "blue" | "yellow" | "gray" ;

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "";
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
interface StatusProps {
  title: string;
  color: AllowedColors;
  description: string;
}

interface Props {
  onActionEditar: (id: number, open: boolean) => void;
  onActionDeletar: (id: number, open: boolean) => void;
}

export const GenerateContratoColumns = ({
  onActionEditar, onActionDeletar
}: Props): GridColDef[] => {

  // --- LÓGICA 1: STATUS (SITUAÇÃO) ---
  // Regra: Se DataAte < Hoje -> Vencido (Vermelho), senão respeita o Status numérico.
  const getStatusProps = (row: any): StatusProps => {
    const hoje = new Date();
    // Zera a hora para comparar apenas a data, se necessário
    hoje.setHours(0,0,0,0);
    
    const dataAte = row.DataAte ? new Date(row.DataAte) : null;
    const isVencido = dataAte && dataAte < hoje;

    // Prioridade: Vencido (V)
    // Regra: Só é vencido se a data passou E o status não for 'Encerrado' (2)
    if (isVencido && row.Status !== 2) { 
      return { title: "V", color: "red", description: "Vencido" };
    }

    // Status Normal (Baseado no Enum do Banco)
    switch (row.Status) {
      case 0: return { title: "EA", color: "green", description: "Em Andamento" };
      case 1: return { title: "P", color: "yellow", description: "Paralisado" };
      case 2: return { title: "E", color: "gray", description: "Encerrado" };
      default: return { title: "?", color: "gray", description: "Desconhecido" };
    }
  };

  // --- LÓGICA 2: DOCUMENTOS ---
  // O Backend deve enviar "DV", "DE" ou "ND" (String)
  const getDocumentosProps = (codigo: string): StatusProps => {
    switch (codigo) {
      case "DV": return { title: "DV", color: "red", description: "Documentos Vencidos" };
      case "DE": return { title: "DE", color: "green", description: "Documentos Em Dia" };
      case "ND": return { title: "ND", color: "yellow", description: "Não possui Documentos" };
      default: return { title: "ND", color: "yellow", description: "Não possui Documentos" };
    }
  };

  // --- LÓGICA 3: CONTRATO ASSINADO ---
  const getAssinadoProps = (status: number): StatusProps => {
    switch (String(status)) {
      case "0": return { title: "AT", color: "green", description: "Assinado por todos" };
      case "3": return { title: "AE", color: "yellow", description: "Apenas a assinatura da empresa" };
      case "1": return { title: "Não", color: "red", description: "Não Assinado" };
      default: return { title: "NA", color: "blue", description: "Não se Aplica" }; 
    }
  };

 return [
    {
      field: "Status",
      headerName: "Status",
      width: 90,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const { title, color, description } = getStatusProps(params.row);
        return (
          <div className="w-full h-full flex justify-center items-center">
            <ItemStatus title={title} color={color} description={description} />
          </div>
        );
      }
    },
    {
      field: "StatusDocumento", 
      headerName: "Docs",
      width: 80,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const { title, color, description } = getDocumentosProps(params.value);
        return (
          <div className="w-full h-full flex justify-center items-center">
            <ItemStatus title={title} color={color} description={description} /> 
          </div>
        );
      }
    },
    {
      field: "ContratoAssinado",
      headerName: "Assinado",
      width: 110, 
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const { title, color, description } = getAssinadoProps(params.value);
        return (
          <div className="w-full h-full flex justify-center items-center">
            <LargeItemStatus title={title} color={color} description={description} />
          </div>
        );
      }
    },
    {
      field: "NumeroContrato",
      headerName: "Nº",
      width: 120,
      renderCell: (params) => {
        return (<div className="w-full h-full flex justify-center items-center">
          <span>{params.value}</span>
        </div>);
      },
    },
    {
      field: "NomeFornecedor",
      headerName: "Fornecedor",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (<div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
          <span>{params.value}</span>
        </div>);
      },
    },
    {
      field: "NomeCentroCusto",
      headerName: "Centro de Custo",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return (<div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
          <span>{params.value}</span>
        </div>);
      },
    },
    {
      field: "DataDe",
      headerName: "Início",
      width: 100,
      renderCell: (params) => { 
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            <span>{dataUtils.dataFormatada(params.value ? params.value : null)}</span>
          </div>
        );
      },
    },
    {
      field: "DataAte",
      headerName: "Término",
      width: 100,
       renderCell: (params) => { 
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            <span>{dataUtils.dataFormatada(params.value ? params.value : null)}</span>
          </div>
        );
      },
    },
    {
      field: "ValorContrato",
      headerName: "Valor",
      width: 120,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center">{formatCurrency(params.value)}</span>);
      },
    },
    {
      field: "ValorMedido",
      headerName: "Medido",
      width: 120,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center">{formatCurrency(params.value)}</span>);
      },
    },
    {
      field: "ValorRestante", 
      headerName: "Restante",
      width: 120,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center">{formatCurrency(params.value)}</span>);
      },
    },
    {
      field: 'IdContrato',
      headerName: 'Ações',
      sortable: false,
      filterable: false,
      width: 100, 
      renderCell: (params) => (
        <div className="flex items-center justify-center w-full h-full gap-2">
          <IconButton onClick={() => onActionEditar(params.value, true)} title="Editar">
            <DynamicIcon nameIcon="EditRounded" size={20} className="text-blue-500" />
          </IconButton>
          <IconButton onClick={() => onActionDeletar(params.value, true)} title="Excluir">
            <DynamicIcon nameIcon="DeleteForeverRounded" size={20} className="text-red-500" />
          </IconButton>
        </div>
      ),
    },
  ];
}